import { STAT_IDS, type StatId, type StatScore } from '@/schemas/stats';

export type PartialStats = Partial<Record<StatId, StatScore>>;

/**
 * Assegna `score` a `targetStat` rimuovendolo prima da qualunque altra
 * stat che lo possiede attualmente ("ruba valore"). Garantisce mutual
 * exclusion per costruzione senza bloccare l'utente.
 *
 * **Pattern "ruba valore"**: se il giocatore clicca un valore già usato
 * altrove, viene trasferito sulla stat target e la stat sorgente torna
 * vuota. Questo permette di cambiare idea sulla distribuzione 16/15/13/12/9/8
 * senza dover prima azzerare manualmente la stat sorgente.
 */
export function stealAssign(
  prev: PartialStats,
  targetStat: StatId,
  score: StatScore,
): PartialStats {
  const next: PartialStats = {};
  for (const id of STAT_IDS) {
    const v = prev[id];
    if (id === targetStat) {
      next[id] = score;
    } else if (v !== score) {
      // Mantieni il valore solo se diverso dal nuovo score
      if (v !== undefined) next[id] = v;
    }
    // else: la stat aveva il valore appena rubato → lasciala vuota
  }
  return next;
}

export function isStatsComplete(p: PartialStats): boolean {
  return STAT_IDS.every((id) => p[id] !== undefined);
}

/**
 * **D9 — FI-23**: rimuove `score` da ogni stat che lo possiede. Caso d'uso:
 * drag&drop del chip dalla stat al pool → l'assegnazione viene rimossa.
 * Operazione idempotente: se il valore non è in nessuna stat, ritorna `prev`
 * con la stessa shape (nuovo riferimento ma stesso contenuto).
 */
export function removeScore(prev: PartialStats, score: StatScore): PartialStats {
  const next: PartialStats = {};
  for (const id of STAT_IDS) {
    const v = prev[id];
    if (v !== undefined && v !== score) next[id] = v;
  }
  return next;
}
